<template>
  <div id="aoetbt">
    <h5>{{ t('rma.BrainTreeCreditCardHeader') }}</h5>
    <div id="aoetbtNameOnCard" class="form-control" />
    <div id="aoetbtCreditCardNumber" class="form-control" />
    <div id="aoetbtCreditCardCVV" class="form-control SecurityCode" />
    <div id="aoetbtCreditCardExpirationDate" class="form-control" />
    <div class="row mt-3">
      <div class="col-12 col-md-3 braintree-charge-label-div">
        {{ t('rma.BrainTreePaymentAmount') }}
      </div>
      <div class="col-12 col-md-3 braintree-charge-amount-div">
        <input
          type="text"
          class="form-control"
          id="aoetbtPaymentAmount"
          v-model="chargeDisplay"
          readonly
          :data-amount="charge"
        />
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12 text-center braintree-submit-button">
        <button id="aoetbtSubmit" class="btn primaryBtn" disabled>{{ t('rma.BrainTreeAddPayment') }}</button>
      </div>
    </div>
    <input type="hidden" id="aoetbtNonce" />
  </div>
</template>
<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { ref, watch } from 'vue'
import { onMounted } from 'vue'
import { useLogger } from 'vue-logger-plugin'
import { RmaPaymentTokenCreate } from '@/api/RMA.ts'
import { GetApiKey, GetOrderEntryConnectorUrl, GetBearerToken } from '@/stores/SettingsStore.ts'
import { AoeBraintreeRma } from './AoeBraintreeRma.ts'
import { FormatCurrency } from '@/services/currencyFormatter'
import { SetIsLoading } from '@/services/AoeHelpers.ts'

const props = defineProps<{
  rmaNumber: string
  charge: number
  name: string
  systemKeyword: string
  check3DS?: boolean
}>()

const logger = useLogger()
logger.debug('BraintreeRmaPaymentEntry props', {
  name: props.name,
  systemKeyword: props.systemKeyword,
  check3DS: props.check3DS === true,
  rmaNumber: props.rmaNumber,
  charge: props.charge,
})

const charge = ref<number>(props.charge ?? 0)
const chargeDisplay = ref<string>(FormatCurrency(charge.value, null) || charge.value.toFixed(2))

watch(() => props.charge, (newCharge) => {
  if (newCharge !== undefined) {
    charge.value = newCharge
    chargeDisplay.value = FormatCurrency(newCharge, null) || newCharge.toFixed(2)
  }
}, { immediate: true })

onMounted(async () => {
  if (!props.rmaNumber) {
    logger.error('props.rmaNumber is missing')
    return
  }

  const response = await RmaPaymentTokenCreate({
    rmaNumber: props.rmaNumber,
    systemKeyword: props.systemKeyword,
    charge: charge.value,
  })

  logger.debug('response:', response)
  if (response.item.ClientToken) {
    AoeBraintreeRma.clientToken = response.item.ClientToken
    AoeBraintreeRma.enableThreeDSecure = props.check3DS === true
    AoeBraintreeRma.hostedFieldsText.cardholderName = t('labels.CardHolderName')
    AoeBraintreeRma.hostedFieldsText.creditCardNumber = t('labels.CardNumber')
    AoeBraintreeRma.hostedFieldsText.cvv = t('labels.CardSecurityCode')
    AoeBraintreeRma.hostedFieldsText.expirationDate = t('labels.CardExpirationDate')
    AoeBraintreeRma.sourceValues.systemKeyword = props.systemKeyword
    AoeBraintreeRma.sourceValues.rmaNumber = props.rmaNumber
    AoeBraintreeRma.createPaymentUrl = await GetOrderEntryConnectorUrl('RmaPaymentCreate')
    AoeBraintreeRma.apiKey = await GetApiKey()
    AoeBraintreeRma.bearerToken = await GetBearerToken()
    AoeBraintreeRma.Initialize()

    await new Promise(resolve => setTimeout(resolve, 100))
    const submitButton = document.querySelector('#aoetbtSubmit')
    if (submitButton) {
      submitButton.addEventListener('click', () => {
        SetIsLoading(true)
        const input = document.querySelector('#aoetbtPaymentAmount') as HTMLInputElement
        if (input) {
          input.value = charge.value.toFixed(2)
        }
      }, { capture: true })
    }

    const messageHandler = (e: MessageEvent) => {
      if (
        e.data &&
        (e.data.messageType === 'aoepaymentcreated' ||
          e.data.messageType === 'aoepaymentcreationerror' ||
          e.data.messageType === 'aoepaymentcreation3dsusercanceled')
      ) {
        SetIsLoading(false)
      }
    }
    window.addEventListener('message', messageHandler)

    return () => {
      window.removeEventListener('message', messageHandler)
    }
  }
})
</script>
<style lang="scss">
#aoetbt {
  .form-control {
    height: calc(1.5em + 0.75rem + 2px);
  }
}
</style>
